/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Button, Checkbox, Icon, Loader } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { useDeviceMediaQuery } from '../UseDeviceMediaQuery/useDeviceMediaQuery';

const UltyIndeterminateCheckboxGroup = ({
  parentLabel,
  options,
  loading,
  onChange,
  showParentCheckbox = true,
  asMobileButton = false,
}) => {
  const useMobileButtonGroup = asMobileButton && !showParentCheckbox;
  const useMobileTwoRowLayout = useMobileButtonGroup && options.length > 2;
  const {isMobile} = useDeviceMediaQuery()

  const handleChangeParentOption = (checked) => {
    const newOptions = [...options.map((option) => ({
      ...option,
      checked,
    }))];
    const checkedValues = newOptions.filter((option) => option.checked).map((option) => option.value);
    onChange({ options: newOptions, checkedValues });
  };

  const handleChangeSubOption = (index, checked) => {
    // Do not mutate an option received from the parent. In particular, this
    // breaks when the parent replaces `options` after an async load because
    // the old and new arrays can share option objects.
    const newOptions = options.map((option, optionIndex) => (
      optionIndex === index ? { ...option, checked } : option
    ));
    const checkedValues = newOptions.filter((option) => option.checked).map((option) => option.value);
    onChange({ options: newOptions, checkedValues });
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        row-gap: 1rem;
      `}
    >
      {showParentCheckbox ? (
        <Checkbox
          css={css`
            font-weight: bold;
          `}
          disabled={loading}
          label={parentLabel}
          checked={options.length > 0 && options.every((option) => option.checked)}
          indeterminate={options.some((option) => option.checked) && !options.every((option) => option.checked)}
          onChange={(e, { checked }) => handleChangeParentOption(checked)}
        />
      ) : (
        <label
          css={css`
            font-weight: bold;
          `}
        >
          {parentLabel}
        </label>
      )}

      {loading && (
        <Loader
          css={css`
            &:before {
              border: 0.2em solid rgba(0, 0, 0, 0.1) !important;
            }
            &:after {
              border-color: #767676 transparent transparent !important;
            }
          `}
          active
          inline
        />
      )}

      {useMobileButtonGroup && isMobile && (
        <div
          css={css`
            display: grid !important;
            gap: 8px;
            grid-template-columns: repeat(2, minmax(0, 1fr));

            ${useMobileTwoRowLayout
              ? `
              & > button:last-of-type {
                grid-column: 1 / -1;
                justify-self: center;
                width: calc(50% - 4px);
              }
            `
              : ''}
          `}
        >
          {options.map((option, index) => (
            <Button
              key={`ulty-mobile-button-group-${parentLabel}-${String(option.value)}`}
              basic
              color={option.checked ? 'teal' : undefined}
              disabled={loading}
              onClick={() => handleChangeSubOption(index, !option.checked)}
              css={css`
                &&& {
                  position: relative;
                  width: 100%;
                  text-align: center !important;
                }
                &&&:focus {
                  color: ${option.checked ? '#00b5ad' : 'rgba(0,0,0,.6)'} !important;
                  background-color: ${option.checked ? 'rgba(0,181,173,.1)' : 'transparent'} !important;
                  border-color: ${option.checked ? '#00b5ad' : 'rgba(34,36,38,.15)'} !important;
                }
              `}
            >
              {option.checked && (
                <Icon
                  name="check"
                  css={css`
                    &&& {
                      position: absolute;
                      right: 0.75em;
                      top: 50%;
                      transform: translateY(-50%);
                      margin: 0;
                    }
                  `}
                />
              )}
              {option.label}
            </Button>
          ))}
        </div>
      )}

      {(!useMobileButtonGroup || !isMobile) && options.map((option, index) => (
        <Checkbox
          css={css`
            margin-left: 1rem;
          `}
          disabled={loading}
          key={`ulty-indeterminate-checkbox-group-${parentLabel}-${String(option.value)}`}
          label={option.label}
          checked={option.checked}
          onChange={(e, { checked }) => handleChangeSubOption(index, checked)}
        />
      ))}
    </div>
  );
};

UltyIndeterminateCheckboxGroup.propTypes = {
  parentLabel: PropTypes.string,
  options: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  showParentCheckbox: PropTypes.bool,
  asMobileButton: PropTypes.bool,
};

export default UltyIndeterminateCheckboxGroup;
